// src/services/superAdminReportService.js
import { repo } from '../dbservice/repo';

class SuperAdminReportService {
  // ── Fetch all companies ──
  async getAllCompanies() {
    try {
      const companies = await repo.fetchData('companies', [], []);
      return companies || [];
    } catch (err) {
      console.error('[SuperAdminReportService] getAllCompanies failed:', err);
      return [];
    }
  }

  // ── Fetch all users ──
  async getAllUsers() {
    try {
      const users = await repo.fetchData('users', [], []);
      return users || [];
    } catch (err) {
      console.error('[SuperAdminReportService] getAllUsers failed:', err);
      return [];
    }
  }

  // ── Fetch all assets ──
  async getAllAssets() {
    try {
      const assets = await repo.fetchData('assets', [], []);
      return assets || [];
    } catch (err) {
      console.error('[SuperAdminReportService] getAllAssets failed:', err);
      return [];
    }
  }

  // ── Convert Firestore/Supabase timestamps to Date ──
  toDate(ts) {
    if (!ts) return null;
    if (ts instanceof Date) return ts;
    if (ts?.toDate) return ts.toDate();
    if (ts?.seconds) return new Date(ts.seconds * 1000);
    if (typeof ts === 'string') return new Date(ts);
    return null;
  }

  // ── Platform-wide analytics ──
  calculatePlatformAnalytics(companies, users, assets) {
    const today = new Date();
    const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
    const oneYearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());

    // Company growth
    const newCompaniesThisMonth = companies.filter((c) => {
      const d = this.toDate(c.createdAt);
      return d && d >= oneMonthAgo;
    }).length;

    const newCompaniesThisYear = companies.filter((c) => {
      const d = this.toDate(c.createdAt);
      return d && d >= oneYearAgo;
    }).length;

    // Asset utilization
    const totalAssets = assets.length;
    const activeAssets = assets.filter(
      (a) => a.status === 'In Use' || a.status === 'Available',
    ).length;
    const utilizationRate =
      totalAssets > 0 ? (activeAssets / totalAssets) * 100 : 0;

    // Maintenance & warranty
    const maintenanceOverdue = assets.filter((a) => {
      const d = this.toDate(a.nextMaintenanceDate);
      return d && d < today;
    }).length;

    const warrantiesExpired = assets.filter((a) => {
      const d = this.toDate(a.warrantyEndDate);
      return d && d < today;
    }).length;

    // Financial
    const totalInvestment = assets.reduce(
      (sum, a) => sum + (parseFloat(a.purchaseValue || a.cost) || 0),
      0,
    );
    const currentValue = assets.reduce((sum, a) => {
      const cost = parseFloat(a.purchaseValue || a.cost) || 0;
      const purchaseDate = this.toDate(a.purchaseDate) || new Date();
      const yearsInUse = Math.max(
        (today - purchaseDate) / (1000 * 60 * 60 * 24 * 365),
        0,
      );
      const depreciation = 0.2 * yearsInUse * cost;
      return sum + Math.max(cost - depreciation, 0);
    }, 0);

    // User engagement
    const activeUsers = users.filter((u) => {
      const d = this.toDate(u.lastLogin);
      return d && d >= oneMonthAgo;
    }).length;

    // Asset type distribution
    const assetTypeMap = {};
    assets.forEach((a) => {
      const type = a.categoryName || a.assetType || 'Uncategorized';
      assetTypeMap[type] = (assetTypeMap[type] || 0) + 1;
    });

    // Company size distribution
    const sizeDistribution = {
      'Small (1-50 assets)': 0,
      'Medium (51-200 assets)': 0,
      'Large (201-500 assets)': 0,
      'Enterprise (500+ assets)': 0,
    };
    companies.forEach((company) => {
      const count = assets.filter((a) => a.companyId === company.id).length;
      if (count <= 50) sizeDistribution['Small (1-50 assets)']++;
      else if (count <= 200) sizeDistribution['Medium (51-200 assets)']++;
      else if (count <= 500) sizeDistribution['Large (201-500 assets)']++;
      else sizeDistribution['Enterprise (500+ assets)']++;
    });

    const locationMap = {};
    companies.forEach((c) => {
      
     if (c.name !== 'N/A' && (c.location || c.city)) { 
        const loc = c.location || c.city;
        locationMap[loc] = (locationMap[loc] || 0) + 1;
      }
    });

    return {
      newCompaniesThisMonth,
      newCompaniesThisYear,
      utilizationRate,
      maintenanceOverdue,
      warrantiesExpired,
      totalInvestment,
      currentValue,
      depreciationRate:
        totalInvestment > 0
          ? ((totalInvestment - currentValue) / totalInvestment) * 100
          : 0,
      activeUsers,
      userEngagementRate:
        users.length > 0 ? (activeUsers / users.length) * 100 : 0,
      assetTypeDistribution: Object.entries(assetTypeMap).map(
        ([type, count]) => ({ type, count }),
      ),
      companySizeDistribution: Object.entries(sizeDistribution).map(
        ([size, count]) => ({ size, count }),
      ),
      locationDistribution: Object.entries(locationMap).map(
        ([location, count]) => ({ location, count }),
      ),
    };
  }

  // ── Benchmarks ──
  calculateBenchmarks(reportData) {
    if (reportData.length === 0) return {};

    const metrics = {
      assetUtilization: reportData.map(
        (c) => (c.utilizedAssets / Math.max(c.totalAssets, 1)) * 100,
      ),
      userToAssetRatio: reportData.map(
        (c) => c.totalUsers / Math.max(c.totalAssets, 1),
      ),
      valuePerAsset: reportData.map(
        (c) => c.totalAssetValue / Math.max(c.totalAssets, 1),
      ),
      adminRatio: reportData.map(
        (c) => (c.adminUsers / Math.max(c.totalUsers, 1)) * 100,
      ),
    };

    const calcStats = (values) => {
      const sorted = [...values].sort((a, b) => a - b);
      return {
        average: values.reduce((s, v) => s + v, 0) / values.length,
        median: sorted[Math.floor(sorted.length / 2)],
        min: Math.min(...values),
        max: Math.max(...values),
      };
    };

    return {
      assetUtilization: calcStats(metrics.assetUtilization),
      userToAssetRatio: calcStats(metrics.userToAssetRatio),
      valuePerAsset: calcStats(metrics.valuePerAsset),
      adminRatio: calcStats(metrics.adminRatio),
    };
  }

  // ── Performance score per company ──
  calculatePerformanceScore(company, benchmarks) {
     if (company.totalAssets === 0) return 0;

    let score = 0;
    let factors = 0;

    if (company.totalAssets > 0) {
      const utilRate =
        (company.utilizedAssets / company.totalAssets) * 100;
      score +=
        Math.min(
          utilRate / (benchmarks.assetUtilization?.average || 1),
          1,
        ) * 30;
      factors += 30;
    }

    if (company.totalAssets > 0) {
      const assignRate =
        (company.assignedAssets / company.totalAssets) * 100;
      score += Math.min(assignRate / 80, 1) * 25;
      factors += 25;
    }

    if (company.totalAssets > 0 && company.totalUsers > 0) {
      const ratio = company.totalUsers / company.totalAssets;
      score +=
        Math.min(
          ratio / (benchmarks.userToAssetRatio?.average || 1),
          1,
        ) * 20;
      factors += 20;
    }

    const depRate =
      company.totalAssetValue > 0
        ? ((company.totalAssetValue - company.depreciatedValue) /
            company.totalAssetValue) *
          100
        : 0;
    score += Math.max(1 - depRate / 50, 0) * 15;
    factors += 15;

    if (company.totalUsers > 0) {
      const adminRatio =
        (company.adminUsers / company.totalUsers) * 100;
      score +=
        Math.min(
          adminRatio / (benchmarks.adminRatio?.average || 10),
          1,
        ) * 10;
      factors += 10;
    }

    return factors > 0 ? (score / factors) * 100 : 0;
  }

  // ── Build per-company report row ──
  buildCompanyReport(company, assets, users) {
    const today = new Date();
    const companyAssets = assets.filter((a) => a.companyId === company.id);
    const companyUsers = users.filter((u) => u.companyId === company.id);

    const totalAssetValue = companyAssets.reduce(
      (sum, a) => sum + (parseFloat(a.purchaseValue || a.cost) || 0),
      0,
    );

    const depreciatedValue = companyAssets.reduce((sum, a) => {
      const cost = parseFloat(a.purchaseValue || a.cost) || 0;
      const purchaseDate = this.toDate(a.purchaseDate) || new Date();
      const yearsInUse = Math.max(
        (today - purchaseDate) / (1000 * 60 * 60 * 24 * 365),
        0,
      );
      return sum + Math.max(cost - 0.2 * yearsInUse * cost, 0);
    }, 0);

    const unassignedAssets = companyAssets.filter(
      (a) => !a.assignedTo || a.assignedTo === '',
    ).length;
    const assignedAssets = companyAssets.filter(
      (a) => a.assignedTo && a.assignedTo !== '',
    ).length;

    const maintenanceIssues = companyAssets.filter((a) => {
      const d = this.toDate(a.nextMaintenanceDate);
      return d && d < today;
    }).length;

    const warrantyIssues = companyAssets.filter((a) => {
      const d = this.toDate(a.warrantyEndDate);
      return d && d < today;
    }).length;

    const averageAssetAge =
      companyAssets.length > 0
        ? companyAssets.reduce((sum, a) => {
            const pd = this.toDate(a.purchaseDate) || new Date();
            return sum + (today - pd) / (1000 * 60 * 60 * 24 * 30);
          }, 0) / companyAssets.length
        : 0;

    return {
      id: company.id,
      name: company.name || 'N/A',
      contactPerson: company.contactPerson || 'N/A',
      email: company.email || 'N/A',
      phone: company.phone || 'N/A',
      location: company.location || company.city || 'N/A',
      createdAt: company.createdAt || null,
      totalAssets: companyAssets.length,
      unassignedAssets,
      assignedAssets,
      utilizedAssets: companyAssets.filter((a) => {
  const status = (a.status || '').toLowerCase();
  return status === 'in use' || status === 'assigned' || status === 'active';
}).length,

      totalAssetValue,
      depreciatedValue,
      totalUsers: companyUsers.length,
      adminUsers: companyUsers.filter((u) => u.role === 'admin').length,
      maintenanceIssues,
      warrantyIssues,
      assetTypes: [
        ...new Set(
          companyAssets.map((a) => a.categoryName || a.assetType),
        ),
      ].length,
      averageAssetAge,
    };
  }

  // ── Master build ──
  async buildReport() {
    const [companies, users, assets] = await Promise.all([
      this.getAllCompanies(),
      this.getAllUsers(),
      this.getAllAssets(),
    ]);

    const analytics = this.calculatePlatformAnalytics(
      companies,
      users,
      assets,
    );

    const reports = companies.map((c) =>
      this.buildCompanyReport(c, assets, users),
    );

    const benchmarks = this.calculateBenchmarks(reports);

    const reportsWithScores = reports.map((r) => ({
      ...r,
      performanceScore: this.calculatePerformanceScore(r, benchmarks),
    }));

    return {
      analytics,
      reports: reportsWithScores,
      benchmarks,
    };
  }
}

const superAdminReportService = new SuperAdminReportService();
export { superAdminReportService };
export default superAdminReportService;